/** Web Push reminders: browser subscription + the user's reminder row in Supabase. */
import { supabase } from './supabase.ts';
import {
  base64UrlToBytes,
  bytesToBase64Url,
  DEFAULT_REMINDER_TIME,
  normalizeReminderTime,
  pushSupport,
  type PushSupport,
} from '../core/push.ts';

const TZ = 'Asia/Almaty';

export interface ReminderSettings {
  enabled: boolean;
  time: string;
}

export function detectPushSupport(): PushSupport {
  const nav = navigator as Navigator & { standalone?: boolean };
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return pushSupport({
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPushManager: 'PushManager' in window,
    hasNotification: 'Notification' in window,
    isIos,
    isStandalone: nav.standalone === true || window.matchMedia('(display-mode: standalone)').matches,
  });
}

export async function loadReminder(userId: string): Promise<ReminderSettings> {
  const { data, error } = await supabase
    .from('reminders')
    .select('enabled, remind_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return { enabled: false, time: DEFAULT_REMINDER_TIME };
  const subscribed = await currentSubscription();
  return { enabled: data.enabled && subscribed !== null, time: normalizeReminderTime(data.remind_at) };
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

async function saveReminderRow(userId: string, settings: ReminderSettings): Promise<void> {
  const { error } = await supabase.from('reminders').upsert(
    { user_id: userId, enabled: settings.enabled, remind_at: settings.time, tz: TZ, updated_at: new Date().toISOString() },
    { onConflict: 'user_id' },
  );
  if (error) throw new Error(error.message);
}

export class PushError extends Error {}

/** Asks for permission, subscribes this device and turns reminders on. */
export async function enableReminders(userId: string, time: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushError('Уведомления запрещены. Разрешите их в настройках телефона для Сөйле.');
  }
  const { data: publicKey, error } = await supabase.rpc('push_vapid_public');
  if (error || !publicKey) throw new PushError('Сервер напоминаний ещё не готов. Попробуйте позже.');

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey) as BufferSource,
    });
  }
  const { error: saveError } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: subscription.endpoint,
      p256dh: bytesToBase64Url(subscription.getKey('p256dh')),
      auth: bytesToBase64Url(subscription.getKey('auth')),
    },
    { onConflict: 'endpoint' },
  );
  if (saveError) throw new Error(saveError.message);
  await saveReminderRow(userId, { enabled: true, time });
}

/** Unsubscribes this device; reminders stay on for the account while another device still has them. */
export async function disableReminders(userId: string, time: string): Promise<void> {
  const subscription = await currentSubscription();
  if (subscription) {
    const { error } = await supabase.from('push_subscriptions').delete().eq('endpoint', subscription.endpoint);
    if (error) throw new Error(error.message);
    await subscription.unsubscribe().catch(() => false);
  }
  const { count } = await supabase
    .from('push_subscriptions')
    .select('endpoint', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (!count) await saveReminderRow(userId, { enabled: false, time });
}

export async function updateReminderTime(userId: string, time: string): Promise<void> {
  await saveReminderRow(userId, { enabled: true, time });
}

/** Sends a test notification to all of this user's devices; returns how many accepted it. */
export async function sendTestReminder(): Promise<number> {
  const { data, error } = await supabase.functions.invoke<{ delivered?: number }>('send-reminders', {
    body: { test: true },
  });
  if (error) throw new Error(error.message);
  return data?.delivered ?? 0;
}
